import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./EditProduct.css";
import { getByIdProduct, updateProductApi } from "../../../services/Products/Product.service";
import { ProductTypes } from "../../../services/Products/Product.types";
import { toast } from "react-toastify";
import axios from "axios";
import { environment } from "../../../environments/environment";

// API Response interfaces
interface UploadResponse {
  success: boolean;
  message: string;
  data: string | string[];
  url?: string;
  imageUrl?: string;
  filePath?: string;
}

interface MetalPurityCaratMaster {
  id: number;
  MetalTypeId: number;
  karat: string | null;
  purity: string | null;
  carat: string | null;
  centValue: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface MetalType {
  id: number;
  name: string;
  description: string;
  status: string;
  createdByRole: string;
  isActive: boolean;
  isDelete: boolean;
  createdAt: string;
  updatedAt: string;
  metal_purity_carat_masters: MetalPurityCaratMaster[];
}

interface Category {
  id: number;
  name: string;
  description: string | null;
  image: string | null;
  status: string;
  createdByRole: string | null;
  isDelete: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UOM {
  id: number;
  name: string;
  description: string;
  status: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Article {
  id: number;
  metalTypeId: number;
  categoryId: number;
  articleName: string;
  description: string | null;
  status: string;
  isActive: boolean;
  isDelete: boolean;
  createdAt: string;
  updatedAt: string;
  metalType: {
    id: number;
    name: string;
  };
  category: {
    id: number;
    name: string;
  };
}

interface Inventory {
  id: number;
  totalQuantity: number;
  totalWeight: number;
  availableQuantity: number;
  availableWeight: number;
  soldQuantity: number;
  soldWeight: number;
  soldRevenue: number;
  previousQuantity: number;
  AddedQuantity: number;
  isAvailable: number;
}

interface ExtendedProduct extends ProductTypes {
  inventory?: Inventory;
  stockStats?: {
    totalQuantity: number;
    totalWeight: number;
    availableQuantity: number;
    availableWeight: number;
    soldQuantity: number;
    soldWeight: number;
    soldRevenue: number;
    previousQuantity: number;
    addedQuantity: number;
    isAvailable: number;
    isInStock: boolean;
    isLowStock: boolean;
    stockPercentage: number;
  };
}

const BASE_URL = environment.fileUrl || "https://api.sonachandiwale.com";

const api = axios.create({
  baseURL: environment.apiBaseUrl || "https://api.sonachandiwale.com/api/v1",
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("isAuthenticated");
      window.location.href = "/login";
      toast.error("Session expired. Please login again.");
    }
    return Promise.reject(error);
  }
);

const EditProduct: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ========== ALL STATES ==========
  const [categories, setCategories] = useState<Category[]>([]);
  const [metalTypes, setMetalTypes] = useState<MetalType[]>([]);
  const [uomList, setUomList] = useState<UOM[]>([]);
  const [allArticles, setAllArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);

  const [productData, setProductData] = useState<Partial<ProductTypes>>({
    description: "",
    categoryId: 0,
    MetalTypeId: 0,
    purity: "",
    quantity: 0,
    wastage: 0,
    makingCharges: 0,
  });

  const [originalProductData, setOriginalProductData] = useState<Partial<ProductTypes>>({});
  const [inventoryData, setInventoryData] = useState<Inventory | null>(null);
  const [availableQuantity, setAvailableQuantity] = useState<number>(0);
  const [availableWeight, setAvailableWeight] = useState<number>(0);
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  const [totalWeight, setTotalWeight] = useState<number>(0);

  const [thumbnailImage, setThumbnailImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");

  const [isT2T, setIsT2T] = useState<boolean>(true);
  const [t2tImage, setT2tImage] = useState<File | null>(null);
  const [uploadedT2TImageUrl, setUploadedT2TImageUrl] = useState<string>("");
  const [difference, setDifference] = useState<number>(0);

  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(true);

  const [selectedArticle, setSelectedArticle] = useState<string>("");
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null);
  const [openArticle, setOpenArticle] = useState<boolean>(false);
  const [searchArticle, setSearchArticle] = useState<string>("");
  const [showOtherArticle, setShowOtherArticle] = useState<boolean>(false);
  const [newArticleValue, setNewArticleValue] = useState<string>("");

  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [openCategory, setOpenCategory] = useState<boolean>(false);
  const [searchCategory, setSearchCategory] = useState<string>("");
  const [showOtherCategory, setShowOtherCategory] = useState<boolean>(false);
  const [newCategoryValue, setNewCategoryValue] = useState<string>("");

  const [selectedMetal, setSelectedMetal] = useState<string>("");
  const [selectedMetalId, setSelectedMetalId] = useState<number | null>(null);
  const [selectedMetalData, setSelectedMetalData] = useState<MetalType | null>(null);
  const [openMetal, setOpenMetal] = useState<boolean>(false);
  const [searchMetal, setSearchMetal] = useState<string>("");

  const [selectedKarat, setSelectedKarat] = useState<string>("");
  const [selectedCarat, setSelectedCarat] = useState<string>("");
  const [purityValue, setPurityValue] = useState<string>("");
  const [centValue, setCentValue] = useState<string>("");
  const [defaultPurity, setDefaultPurity] = useState<string>("");

  const [availableKaratOptions, setAvailableKaratOptions] = useState<{ karat: string, purity: string }[]>([]);
  const [availableCaratOptions, setAvailableCaratOptions] = useState<{ carat: string, cent: number }[]>([]);
  const [availableSilverPurities, setAvailableSilverPurities] = useState<string[]>([]);

  const [quantityType, setQuantityType] = useState<string>("gm");
  const [weightPerGram, setWeightPerGram] = useState<number | "">(0);
  const [quantityValue, setQuantityValue] = useState<number | "">(0);
  const [selectedUomId, setSelectedUomId] = useState<number>(1);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);

  const [dimensions, setDimensions] = useState<{
    length: number | "";
    width: number | "";
    height: number | "";
    diameter: number | "";
  }>({
    length: 0,
    width: 0,
    height: 0,
    diameter: 0,
  });

  const [originalDimensions, setOriginalDimensions] = useState({
    length: 0,
    width: 0,
    height: 0,
    diameter: 0,
  });

  const [availabilityStatus, setAvailabilityStatus] = useState<string>("Available");
  const [status, setStatus] = useState<string>("pending");
  const specialMetals = ["gold", "silver", "diamond"];

  const [isDataReady, setIsDataReady] = useState(false);

  // ========== HELPER FUNCTIONS ==========
  const extractImageUrl = (data: string | string[] | undefined): string | null => {
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (Array.isArray(data) && data.length > 0) return data[0];
    return null;
  };

  const getFullImageUrl = (imagePath: string): string => {
    if (!imagePath) return "";
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    if (imagePath.startsWith('data:')) {
      return imagePath;
    }
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    return `${BASE_URL}/${cleanPath}`;
  };

  // ========== FETCH ALL DATA ==========
  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [categoriesRes, metalsRes, uomRes, articlesRes] = await Promise.all([
        api.get("/activeCategories").catch(() => ({ data: { data: [] } })),
        api.get("/metal/Activelist").catch(() => ({ data: { data: [] } })),
        api.get("/admin/get_All_UOM").catch(() => ({ data: { data: [] } })),
        api.get("/admin/Activearticles").catch(() => ({ data: { data: [] } })),
      ]);

      if (categoriesRes.data?.data) {
        setCategories(categoriesRes.data.data);
      } else if (Array.isArray(categoriesRes.data)) {
        setCategories(categoriesRes.data);
      }

      if (metalsRes.data?.data) {
        setMetalTypes(metalsRes.data.data);
      } else if (Array.isArray(metalsRes.data)) {
        setMetalTypes(metalsRes.data);
      }

      // ✅ UOM - Like Deal Calculator and Add Product
      if (uomRes.data?.data) {
        setUomList(uomRes.data.data);
        if (uomRes.data.data.length > 0) {
          setSelectedUomId(uomRes.data.data[0].id);
          setQuantityType(uomRes.data.data[0].name.toLowerCase());
        }
      } else if (Array.isArray(uomRes.data)) {
        setUomList(uomRes.data);
        if (uomRes.data.length > 0) {
          setSelectedUomId(uomRes.data[0].id);
          setQuantityType(uomRes.data[0].name.toLowerCase());
        }
      }

      if (articlesRes.data?.data) {
        setAllArticles(articlesRes.data.data);
      } else if (Array.isArray(articlesRes.data)) {
        setAllArticles(articlesRes.data);
      }

      setIsDataReady(true);

    } catch (error: any) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load master data");
    } finally {
      setIsLoading(false);
    }
  };

  // ========== FETCH PRODUCT DATA ==========
  const fetchProductData = async () => {
    if (!id) return;

    setIsFetching(true);
    try {
      const response = await getByIdProduct(id) as ExtendedProduct;
      console.log("Product data:", response);

      setProductData(response);
      setOriginalProductData(response);

      // Set inventory data
      if (response.inventory) {
        setInventoryData(response.inventory);
        setAvailableQuantity(response.inventory.availableQuantity || 0);
        setAvailableWeight(response.inventory.availableWeight || 0);
        setTotalQuantity(response.inventory.totalQuantity || 0);
        setTotalWeight(response.inventory.totalWeight || 0);
        setQuantityValue(response.inventory.availableQuantity || 0);
      } else {
        setQuantityValue(response.quantity || 0);
      }

      // Set metal
      if (response.MetalTypeId) {
        setSelectedMetalId(response.MetalTypeId);
        const metal = metalTypes.find(m => m.id === response.MetalTypeId);
        if (metal) {
          setSelectedMetal(metal.name);
          setSelectedMetalData(metal);
        } else {
          setSelectedMetal(String(response.MetalTypeId));
        }
      }

      // Set category
      if (response.categoryId) {
        setSelectedCategoryId(response.categoryId);
        const cat = categories.find(c => c.id === response.categoryId);
        if (cat) {
          setSelectedCategory(cat.name);
        } else if (response.category_name) {
          setSelectedCategory(response.category_name);
        }
      } else if (response.category_name) {
        setSelectedCategory(response.category_name);
      }

      // Set article
      if (response.ArticleId) {
        setSelectedArticleId(response.ArticleId);
        const article = allArticles.find(a => a.id === response.ArticleId);
        if (article) {
          setSelectedArticle(article.articleName);
        } else if (response.articleName) {
          setSelectedArticle(response.articleName);
        }
      } else if (response.articleName) {
        setSelectedArticle(response.articleName);
      }

      // Set purity/karat/carat
      if (response.purity) setPurityValue(response.purity);
      if (response.karat) setSelectedKarat(response.karat);
      if (response.carat) setSelectedCarat(response.carat);
      if (response.centValue) setCentValue(response.centValue?.toString() || "");

      // Set UOM
      if (response.UomId) {
        setSelectedUomId(response.UomId);
        const uom = uomList.find(u => u.id === response.UomId);
        if (uom) setQuantityType(uom.name.toLowerCase());
      }

      // Set weight
      if (response.inventory?.availableWeight !== undefined) {
        setWeightPerGram(response.inventory.availableWeight);
      } else if (response.weight) {
        const weightNum = parseFloat(response.weight.toString().replace('gm', ''));
        setWeightPerGram(weightNum);
      }

      // Set dimensions
      if (response.Dimensions && response.Dimensions.length > 0) {
        const dim = response.Dimensions[0];
        const dims = {
          length: dim.length ? parseFloat(dim.length.toString()) || 0 : 0,
          width: dim.width ? parseFloat(dim.width.toString()) || 0 : 0,
          height: dim.height ? parseFloat(dim.height.toString()) || 0 : 0,
          diameter: dim.diameter ? parseFloat(dim.diameter.toString()) || 0 : 0,
        };
        setDimensions(dims);
        setOriginalDimensions(dims);
      }

      // Set T2T
      if (response.t2t !== undefined) setIsT2T(response.t2t);

      // Set difference
      if (response.difference) {
        const diffNum = parseFloat(response.difference.toString().replace('gm', ''));
        setDifference(diffNum);
      }

      // Set images
      if (response.image) {
        const imageUrl = extractImageUrl(response.image);
        if (imageUrl) {
          const fullUrl = getFullImageUrl(imageUrl);
          setUploadedImageUrl(fullUrl);
          setPreviewImage(fullUrl);
        }
      }

      if (response.tunchImage) {
        const tunchUrl = extractImageUrl(response.tunchImage);
        if (tunchUrl) {
          const fullUrl = getFullImageUrl(tunchUrl);
          setUploadedT2TImageUrl(fullUrl);
        }
      }

      // Set availability
      if (response.availabilityStatus) {
        setAvailabilityStatus(response.availabilityStatus);
      }

      // Set status
      if (response.status) {
        setStatus(response.status);
      }

    } catch (error) {
      console.error("Failed to fetch product:", error);
      toast.error("Failed to load product data");
    } finally {
      setIsFetching(false);
    }
  };

  // ========== MAIN EFFECT ==========
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login first");
      navigate("/login");
      return;
    }
    fetchAllData();
  }, [navigate]);

  // ========== FETCH PRODUCT AFTER MASTER DATA IS READY ==========
  useEffect(() => {
    if (isDataReady && id) {
      fetchProductData();
    }
  }, [isDataReady, id, metalTypes, categories, allArticles, uomList]);

  // ========== FILTER CATEGORY BASED ON METAL ==========
  useEffect(() => {
    if (selectedMetalId) {
      const relatedCategories = categories.filter((category) =>
        allArticles.some(
          (article) =>
            article.metalTypeId === selectedMetalId &&
            article.categoryId === category.id
        )
      );
      setFilteredCategories(relatedCategories);
    } else {
      setFilteredCategories(categories);
    }
  }, [selectedMetalId, categories, allArticles]);

  // ========== FILTER ARTICLES ==========
  useEffect(() => {
    if (selectedMetalId && selectedCategoryId) {
      const filtered = allArticles.filter(
        article => article.metalTypeId === selectedMetalId && article.categoryId === selectedCategoryId
      );
      setFilteredArticles(filtered);
    } else if (selectedMetalId) {
      const filtered = allArticles.filter(article => article.metalTypeId === selectedMetalId);
      setFilteredArticles(filtered);
    } else if (selectedCategoryId) {
      const filtered = allArticles.filter(article => article.categoryId === selectedCategoryId);
      setFilteredArticles(filtered);
    } else {
      setFilteredArticles(allArticles);
    }
  }, [selectedMetalId, selectedCategoryId, allArticles]);

  // ========== UPDATE AVAILABLE OPTIONS WHEN METAL CHANGES ==========
  useEffect(() => {
    if (selectedMetalData && selectedMetalData.metal_purity_carat_masters) {
      const masters = selectedMetalData.metal_purity_carat_masters;
      const metalName = selectedMetalData.name.toLowerCase();

      if (metalName === "gold") {
        const karatOptions = masters
          .filter(m => m.karat !== null && m.purity !== null)
          .map(m => ({
            karat: m.karat as string,
            purity: m.purity as string
          }));
        setAvailableKaratOptions(karatOptions);
        setAvailableCaratOptions([]);
        setAvailableSilverPurities([]);
      } else if (metalName === "diamond") {
        const caratOptions = masters
          .filter(m => m.carat !== null && m.centValue !== null)
          .map(m => ({
            carat: m.carat as string,
            cent: m.centValue as number
          }));
        setAvailableCaratOptions(caratOptions);
        setAvailableKaratOptions([]);
        setAvailableSilverPurities([]);
      } else if (metalName === "silver") {
        const purities = masters
          .filter(m => m.purity !== null)
          .map(m => m.purity as string);
        setAvailableSilverPurities(purities);
        setAvailableKaratOptions([]);
        setAvailableCaratOptions([]);
      }
    }
  }, [selectedMetalData]);

  // ========== IMAGE UPLOAD FUNCTION ==========
  const uploadImage = async (file: File, type: 'product' | 't2t'): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await api.post<UploadResponse>("/uplordDoc", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        let imageUrl = extractImageUrl(response.data.data);
        if (!imageUrl && response.data.url) imageUrl = response.data.url;
        if (!imageUrl && response.data.imageUrl) imageUrl = response.data.imageUrl;
        if (!imageUrl && response.data.filePath) imageUrl = response.data.filePath;

        if (!imageUrl) throw new Error("No image URL returned from server");

        const fullImageUrl = getFullImageUrl(imageUrl);
        toast.success(`${type === 'product' ? 'Product' : 'T2T'} image uploaded successfully`);
        return fullImageUrl;
      } else {
        throw new Error(response.data.message || "Image upload failed");
      }
    } catch (error: any) {
      console.error("Image upload error:", error);
      toast.error(error.response?.data?.message || "Failed to upload image");
      throw error;
    }
  };

  // ========== HANDLE IMAGE CHANGE ==========
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setThumbnailImage(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result as string);
      reader.readAsDataURL(file);

      try {
        setIsUploading(true);
        const imageUrl = await uploadImage(file, 'product');
        setUploadedImageUrl(imageUrl);
        setPreviewImage(imageUrl);
      } catch (error) {
        setThumbnailImage(null);
        setPreviewImage(null);
      } finally {
        setIsUploading(false);
      }
    }
  };

  // ========== HANDLE T2T IMAGE CHANGE ==========
  const handleT2TImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setT2tImage(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewElement = document.getElementById('t2t-preview') as HTMLImageElement;
        if (previewElement) {
          previewElement.src = reader.result as string;
          previewElement.style.display = 'block';
        }
      };
      reader.readAsDataURL(file);

      try {
        setIsUploading(true);
        const imageUrl = await uploadImage(file, 't2t');
        setUploadedT2TImageUrl(imageUrl);
      } catch (error) {
        setT2tImage(null);
      } finally {
        setIsUploading(false);
      }
    }
  };

  // ========== HANDLE CHANGE ==========
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "length" || name === "width" || name === "height" || name === "diameter") {
      setDimensions(prev => ({
        ...prev,
        [name]: value === "" ? "" : Number(value)
      }));
    } else if (name === "wastage" || name === "makingCharges") {
      setProductData((prev) => ({
        ...prev,
        [name]: value === "" ? "" : Number(value)
      }));
    } else {
      setProductData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // ========== METAL HANDLERS ==========
  const handleKaratSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedKaratValue = e.target.value;
    setSelectedKarat(selectedKaratValue);
    const selected = availableKaratOptions.find(opt => opt.karat === selectedKaratValue);
    if (selected) setPurityValue(selected.purity);
  };

  const handlePurityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPurityValue(e.target.value);
  };

  const handleCaratSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCaratValue = e.target.value;
    setSelectedCarat(selectedCaratValue);
    const selected = availableCaratOptions.find(opt => opt.carat === selectedCaratValue);
    if (selected) setCentValue(selected.cent.toString());
  };

  const handleCentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCentValue(e.target.value);
  };

  const handleSilverPurityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPurityValue(e.target.value);
  };

  // ========== T2T HANDLERS ==========
  const increaseDifference = () => {
    setDifference((prev) => +(prev + 0.1).toFixed(1));
  };

  const decreaseDifference = () => {
    setDifference((prev) => +(prev - 0.1).toFixed(1));
  };

  // ========== QUANTITY HANDLERS ==========
  const handleQuantityTypeChange = (type: string) => {
    setQuantityType(type);
    const uom = uomList.find(u => u.name.toLowerCase() === type.toLowerCase());
    if (uom) setSelectedUomId(uom.id);
  };

  const handleQuantityValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuantityValue(value === "" ? "" : Number(value));
    if (inventoryData) setAvailableQuantity(value === "" ? 0 : Number(value));
  };

  // ========== SUBMIT HANDLER ==========
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!id) return;

  if (!selectedMetalId) {
    toast.error("Please select metal type");
    return;
  }

  if (!selectedCategoryId && !selectedCategory) {
    toast.error("Please select category");
    return;
  }

  if (!selectedArticleId && !selectedArticle) {
    toast.error("Please select article");
    return;
  }

  if (thumbnailImage && !uploadedImageUrl) {
    toast.error("Please wait for product image to upload");
    return;
  }

  if (!isT2T && t2tImage && !uploadedT2TImageUrl) {
    toast.error("Please wait for T2T image to upload");
    return;
  }

  try {
    setIsSubmitting(true);

    const requestBody: Record<string, any> = {};
    const original = originalProductData;

    const hasChanged = (current: any, original: any): boolean => {
      if (current == null && original == null) return false;
      if (current == null || original == null) return true;
      if (typeof current === 'object' && typeof original === 'object') {
        return JSON.stringify(current) !== JSON.stringify(original);
      }
      if (!isNaN(parseFloat(current)) && !isNaN(parseFloat(original))) {
        return parseFloat(current) !== parseFloat(original);
      }
      return current !== original;
    };

    // 1. Quantity
    if (hasChanged(quantityValue, original.quantity || 0)) {
      requestBody.quantity = quantityValue;
    }

    // 2. UOM
    if (hasChanged(selectedUomId, original.UomId || 1)) {
      requestBody.UomId = selectedUomId;
    }

    // 3. Description
    if (hasChanged(productData.description || "", original.description || "")) {
      requestBody.description = productData.description || "";
    }

    // 4. MetalTypeId
    if (hasChanged(selectedMetalId, original.MetalTypeId)) {
      requestBody.MetalTypeId = selectedMetalId;
    }

    // 5. Weight
    const newWeight = Number(weightPerGram) > 0 ? `${weightPerGram}` : "";
    const oldWeight = original.weight ? `${original.weight}` : "";
    if (hasChanged(newWeight, oldWeight)) {
      requestBody.weight = newWeight;
    }

    // 6. Dimensions
    const newDimensions = dimensions.length || dimensions.width || dimensions.height || dimensions.diameter
      ? [{
          length: dimensions.length || 0,
          width: dimensions.width || 0,
          height: dimensions.height || 0,
          diameter: dimensions.diameter || 0,
        }]
      : [];
    const oldDimensions = original.Dimensions || [];
    if (JSON.stringify(newDimensions) !== JSON.stringify(oldDimensions)) {
      requestBody.Dimensions = newDimensions;
    }

    // 7. Wastage
    if (hasChanged(productData.wastage || 0, original.wastage || 0)) {
      requestBody.wastage = productData.wastage || 0;
    }

    // 8. Making Charges
    if (hasChanged(productData.makingCharges || 0, original.makingCharges || 0)) {
      requestBody.makingCharges = productData.makingCharges || 0;
    }

    // 9. T2T
    if (hasChanged(isT2T, original.t2t ?? true)) {
      requestBody.t2t = isT2T;
    }

    // 10. Difference
    const newDifference = `${difference}gm`;
    const oldDifference = original.difference || "0gm";
    if (hasChanged(newDifference, oldDifference)) {
      requestBody.difference = newDifference;
    }

    // 11. Availability Status
    if (hasChanged(availabilityStatus, original.availabilityStatus || "Available")) {
      requestBody.availabilityStatus = availabilityStatus;
    }

    // 12. Status
    if (hasChanged(status, original.status || "pending")) {
      requestBody.status = status;
    }

    // 13. Image - ✅ Convert to array
    if (uploadedImageUrl) {
      let cleanImagePath = uploadedImageUrl;
      if (cleanImagePath.startsWith(BASE_URL)) {
        cleanImagePath = cleanImagePath.replace(BASE_URL, '');
      }
      if (cleanImagePath.startsWith('/')) {
        cleanImagePath = cleanImagePath.slice(1);
      }
      
      // ✅ Send as array
      const imageArray = [cleanImagePath];
      
      const oldImage = original.image ? 
        (Array.isArray(original.image) ? original.image[0] : String(original.image)).replace(BASE_URL, '') 
        : '';
      
      if (hasChanged(cleanImagePath, oldImage)) {
        requestBody.image = imageArray;
      }
    }

    // 14. T2T Image - ✅ Convert to array
    if (uploadedT2TImageUrl) {
      let cleanT2TPath = uploadedT2TImageUrl;
      if (cleanT2TPath.startsWith(BASE_URL)) {
        cleanT2TPath = cleanT2TPath.replace(BASE_URL, '');
      }
      if (cleanT2TPath.startsWith('/')) {
        cleanT2TPath = cleanT2TPath.slice(1);
      }
      
      // ✅ Send as array
      const t2tArray = [cleanT2TPath];
      
      const oldT2T = original.tunchImage ? 
        (Array.isArray(original.tunchImage) ? original.tunchImage[0] : String(original.tunchImage)).replace(BASE_URL, '') 
        : '';
      
      if (hasChanged(cleanT2TPath, oldT2T)) {
        requestBody.tunchImage = t2tArray;
      }
    }

    // 15. Category
    if (selectedCategoryId) {
      if (hasChanged(selectedCategoryId, original.categoryId)) {
        requestBody.categoryId = selectedCategoryId;
      }
    } else if (selectedCategory) {
      if (hasChanged(selectedCategory, original.category_name || '')) {
        requestBody.category_name = selectedCategory;
      }
    }

    // 16. Article
    if (selectedArticleId) {
      if (hasChanged(selectedArticleId, original.ArticleId)) {
        requestBody.ArticleId = selectedArticleId;
      }
    } else if (selectedArticle) {
      if (hasChanged(selectedArticle, original.articleName || '')) {
        requestBody.articleName = selectedArticle;
      }
    }

    // 17. Purity fields
    if (selectedMetalData) {
      const metalName = selectedMetalData.name.toLowerCase();

      if (metalName === "gold") {
        if (hasChanged(purityValue || null, original.purity || null)) {
          requestBody.purity = purityValue || null;
        }
        if (hasChanged(selectedKarat || null, original.karat || null)) {
          requestBody.karat = selectedKarat || null;
        }
      } else if (metalName === "silver") {
        if (hasChanged(purityValue || null, original.purity || null)) {
          requestBody.purity = purityValue || null;
        }
      } else if (metalName === "diamond") {
        if (hasChanged(selectedCarat || null, original.carat || null)) {
          requestBody.carat = selectedCarat || null;
        }
        if (hasChanged(centValue ? Number(centValue) : null, original.centValue || null)) {
          requestBody.centValue = centValue ? Number(centValue) : null;
        }
      } else {
        if (hasChanged(defaultPurity || null, original.purity || null)) {
          requestBody.purity = defaultPurity || null;
        }
      }
    }

    if (Object.keys(requestBody).length === 0) {
      toast.info("No changes detected");
      setIsSubmitting(false);
      return;
    }

    console.log("📤 Sending only changed fields:", requestBody);

    await updateProductApi(id, requestBody);

    toast.success("Product Updated Successfully");
    navigate("/product/list");

  } catch (error: any) {
    console.error("API Error:", error);
    const errorMessage = error.response?.data?.message || error.message || "Failed to update product";
    toast.error(errorMessage);
  } finally {
    setIsSubmitting(false);
  }
};

  // ========== ARTICLE HANDLERS ==========
  const handleArticleSelect = (article: Article) => {
    setSelectedArticle(article.articleName);
    setSelectedArticleId(article.id);
    setOpenArticle(false);
    setShowOtherArticle(false);
  };

  const saveOtherArticle = () => {
    if (!newArticleValue.trim()) return;
    setSelectedArticle(newArticleValue);
    setSelectedArticleId(null);
    setOpenArticle(false);
    setShowOtherArticle(false);
    setNewArticleValue("");
  };

  const getFilteredArticlesForDropdown = () => {
    return filteredArticles.filter((a) =>
      a.articleName.toLowerCase().includes(searchArticle.toLowerCase())
    );
  };

  // ========== CATEGORY HANDLERS ==========
  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category.name);
    setSelectedCategoryId(category.id);
    setProductData(prev => ({
      ...prev,
      categoryId: category.id
    }));
    setOpenCategory(false);
    setShowOtherCategory(false);
  };

  const saveOtherCategory = () => {
    if (!newCategoryValue.trim()) return;
    setSelectedCategory(newCategoryValue);
    setSelectedCategoryId(null);
    setProductData(prev => ({
      ...prev,
      categoryId: 0
    }));
    setOpenCategory(false);
    setShowOtherCategory(false);
    setNewCategoryValue("");
  };

  const filteredCategoriesList = filteredCategories.filter((cat) =>
    cat.name.toLowerCase().includes(searchCategory.toLowerCase())
  );

  // ========== METAL HANDLERS ==========
  const handleMetalSelect = (metal: MetalType) => {
    setSelectedMetal(metal.name);
    setSelectedMetalId(metal.id);
    setSelectedMetalData(metal);
    setProductData(prev => ({
      ...prev,
      MetalTypeId: metal.id
    }));
    setOpenMetal(false);
    setSelectedCategory("");
    setSelectedCategoryId(null);
    setSelectedArticle("");
    setSelectedArticleId(null);
    setSelectedKarat("");
    setSelectedCarat("");
    setPurityValue("");
    setCentValue("");
    setDefaultPurity("");
  };

  const filteredMetalsList = metalTypes.filter((metal) =>
    metal.name.toLowerCase().includes(searchMetal.toLowerCase())
  );

  const isSpecialMetal = (metalName: string) =>
    specialMetals.includes(metalName.toLowerCase());

  // ========== RENDER PURITY FIELDS ==========
  const renderPurityFields = () => {
    if (!selectedMetalData) return null;

    const metalName = selectedMetalData.name.toLowerCase();

    if (metalName === "gold") {
      return (
        <>
          <div className="custom-dropdown-container" style={{ gridColumn: 'span 1' }}>
            <label>Karat *</label>
            <select
              value={selectedKarat}
              onChange={handleKaratSelect}
              className="custom-select"
              required
              style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="">Select Karat</option>
              {availableKaratOptions.map((option, index) => (
                <option key={`karat-${index}`} value={option.karat}>
                  {option.karat}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Purity (%) *</label>
            <input
              type="text"
              value={purityValue}
              onChange={handlePurityChange}
              placeholder="Enter purity %"
              required
            />
          </div>
        </>
      );
    } else if (metalName === "silver") {
      return (
        <div>
          <label>Purity (%) *</label>
          <input
            type="text"
            placeholder="Enter purity %"
            value={purityValue}
            onChange={handleSilverPurityChange}
            required
          />
        </div>
      );
    } else if (metalName === "diamond") {
      return (
        <>
          <div className="custom-dropdown-container" style={{ gridColumn: 'span 1' }}>
            <label>Carat *</label>
            <select
              value={selectedCarat}
              onChange={handleCaratSelect}
              className="custom-select"
              required
              style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="">Select Carat</option>
              {availableCaratOptions.map((option, index) => (
                <option key={`carat-${index}`} value={option.carat}>
                  {option.carat}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Cent Value *</label>
            <input
              type="text"
              value={centValue}
              onChange={handleCentChange}
              placeholder="Enter cent value"
              required
            />
          </div>
        </>
      );
    }

    return (
      <div style={{ gridColumn: 'span 2' }}>
        <label>Purity/Karat/Cent</label>
        <input
          type="text"
          placeholder="Enter value"
          value={defaultPurity}
          onChange={(e) => setDefaultPurity(e.target.value)}
        />
      </div>
    );
  };

  // ========== LOADING STATES ==========
  if (isLoading || isFetching) {
    return <div className="loading-container">Loading...</div>;
  }

  // ========== RENDER ==========
  return (
    <div className="add-product-container">
      <form onSubmit={handleSubmit} className="add-product-form">
        {/* BASIC INFO */}
        <div className="form-section">
          <h3>Edit Product</h3>
          <div className="form-grid">
            {/* Metal Type */}
            <div className="custom-dropdown-container">
              <label>Metal Type *</label>
              <div className="custom-dropdown">
                <div
                  className="custom-dropdown-select"
                  onClick={() => setOpenMetal(!openMetal)}
                >
                  {selectedMetal || "Select Metal Type"}
                </div>
                {openMetal && (
                  <div className="custom-dropdown-menu">
                    <input
                      type="text"
                      className="custom-dropdown-search"
                      placeholder="Search metal type..."
                      value={searchMetal}
                      onChange={(e) => setSearchMetal(e.target.value)}
                      autoFocus
                    />
                    <div className="custom-dropdown-options">
                      {filteredMetalsList.length > 0 ? (
                        filteredMetalsList.map((metal) => (
                          <div
                            key={metal.id}
                            className="custom-dropdown-option"
                            onClick={() => handleMetalSelect(metal)}
                          >
                            {metal.name}
                          </div>
                        ))
                      ) : (
                        <div className="custom-dropdown-option disabled">
                          No metal types found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Category */}
            <div className="custom-dropdown-container">
              <label>Category *</label>
              <div className="custom-dropdown">
                <div
                  className="custom-dropdown-select"
                  onClick={() => setOpenCategory(!openCategory)}
                >
                  {selectedCategory || "Select Category"}
                </div>
                {openCategory && (
                  <div className="custom-dropdown-menu">
                    <input
                      type="text"
                      className="custom-dropdown-search"
                      placeholder="Search category..."
                      value={searchCategory}
                      onChange={(e) => setSearchCategory(e.target.value)}
                      autoFocus
                    />
                    <div className="custom-dropdown-options">
                      {filteredCategoriesList.length > 0 ? (
                        filteredCategoriesList.map((cat) => (
                          <div
                            key={cat.id}
                            className="custom-dropdown-option"
                            onClick={() => handleCategorySelect(cat)}
                          >
                            {cat.name}
                          </div>
                        ))
                      ) : (
                        <div className="custom-dropdown-option disabled">
                          No categories found
                        </div>
                      )}
                      {!showOtherCategory ? (
                        <div
                          className="custom-dropdown-option other-option"
                          onClick={() => setShowOtherCategory(true)}
                        >
                          + Other
                        </div>
                      ) : (
                        <div className="custom-dropdown-other-input">
                          <input
                            type="text"
                            value={newCategoryValue}
                            onChange={(e) => setNewCategoryValue(e.target.value)}
                            placeholder="Enter new category"
                            className="other-text-input"
                          />
                          <button
                            type="button"
                            onClick={saveOtherCategory}
                            className="other-save-btn"
                          >
                            Save
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Article Name */}
            <div className="custom-dropdown-container">
              <label>Article Name *</label>
              <div className="custom-dropdown">
                <div
                  className="custom-dropdown-select"
                  onClick={() => setOpenArticle(!openArticle)}
                >
                  {selectedArticle || "Select Article"}
                </div>
                {openArticle && (
                  <div className="custom-dropdown-menu">
                    <input
                      type="text"
                      className="custom-dropdown-search"
                      placeholder="Search article..."
                      value={searchArticle}
                      onChange={(e) => setSearchArticle(e.target.value)}
                      autoFocus
                    />
                    <div className="custom-dropdown-options">
                      {getFilteredArticlesForDropdown().length > 0 ? (
                        getFilteredArticlesForDropdown().map((article) => (
                          <div
                            key={article.id}
                            className="custom-dropdown-option"
                            onClick={() => handleArticleSelect(article)}
                          >
                            {article.articleName}
                          </div>
                        ))
                      ) : (
                        <div className="custom-dropdown-option disabled">
                          No articles found
                        </div>
                      )}
                      {!showOtherArticle ? (
                        <div
                          className="custom-dropdown-option other-option"
                          onClick={() => setShowOtherArticle(true)}
                        >
                          + Other
                        </div>
                      ) : (
                        <div className="custom-dropdown-other-input">
                          <input
                            type="text"
                            value={newArticleValue}
                            onChange={(e) => setNewArticleValue(e.target.value)}
                            placeholder="Enter new article"
                            className="other-text-input"
                          />
                          <button
                            type="button"
                            onClick={saveOtherArticle}
                            className="other-save-btn"
                          >
                            Save
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Purity fields based on metal selection */}
            {selectedMetal && selectedMetalData && renderPurityFields()}

            {/* Weight */}
            <div>
              <label>Weight (Available) *</label>
              <input
                type="number"
                value={String(weightPerGram) === "0" || weightPerGram === "" ? "" : weightPerGram}
                onChange={(e) => setWeightPerGram(e.target.value === "" ? "" : Number(e.target.value))}
                step="0.01"
                placeholder="Enter weight"
                required
              />
              {inventoryData && (
                <small style={{ color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  Total Weight in Inventory: {totalWeight} {quantityType}
                </small>
              )}
            </div>

            {/* Extra purity for non-special metals */}
            {(!selectedMetal || (selectedMetal && selectedMetalData && !isSpecialMetal(selectedMetal))) && (
              <div>
                <label>Purity%/carat/karat*</label>
                <input
                  type="text"
                  placeholder="Enter purity"
                  value={purityValue}
                  onChange={handleSilverPurityChange}
                  required
                />
              </div>
            )}
          </div>
        </div>

        {/* PRODUCT DETAILS */}
        <div className="form-section">
          <h3>Product Details</h3>
          <div className="form-grid">
            <div>
              <label>Available Quantity *</label>
              <input
                type="number"
                placeholder="Enter available quantity"
                value={quantityValue === 0 || quantityValue === "" ? "" : quantityValue}
                onChange={handleQuantityValueChange}
                className="quantity-input"
                min="0"
                step="0.001"
                required
              />
              <div className="quantity-cards">
                {uomList.map((uom) => (
                  <button
                    key={uom.id}
                    type="button"
                    className={`quantity-card ${quantityType === uom.name.toLowerCase() ? "active" : ""}`}
                    onClick={() => handleQuantityTypeChange(uom.name.toLowerCase())}
                  >
                    <span className="quantity-label">{uom.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {selectedMetalData?.name?.toLowerCase() !== "diamond" ? (
              <>
                <div>
                  <label>Wastage</label>
                  <input
                    type="number"
                    name="wastage"
                    value={String(productData.wastage) === "0" || productData.wastage === undefined ? "" : productData.wastage}
                    onChange={handleChange}
                    step="0.01"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label>Making Charges</label>
                  <input
                    type="number"
                    name="makingCharges"
                    value={String(productData.makingCharges) === "0" || productData.makingCharges === undefined ? "" : productData.makingCharges}
                    onChange={handleChange}
                    step="0.01"
                    placeholder="0"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label>Making Charges</label>
                  <input
                    type="number"
                    name="makingCharges"
                    value={productData.makingCharges || 0}
                    onChange={handleChange}
                    step="0.01"
                    placeholder="0"
                  />
                </div>
                <div></div>
              </>
            )}

            {/* T2T Section */}
            <div className="form-section full-width">
              <h3>Tunch to Tunch (T2T)</h3>
              <div className="toggle-buttons">
                <button
                  type="button"
                  className={isT2T ? "active-btn" : ""}
                  onClick={() => setIsT2T(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={!isT2T ? "active-btn" : ""}
                  onClick={() => setIsT2T(false)}
                >
                  No
                </button>
              </div>

              {!isT2T && (
                <div className="t2t-fields-grid">
                  <div className="image-upload-box small">
                    {uploadedT2TImageUrl ? (
                      <>
                        <img
                          id="t2t-preview"
                          src={uploadedT2TImageUrl}
                          alt="T2T Preview"
                          style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover' }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                        <small style={{ color: '#10b981', display: 'block', marginTop: '4px' }}>
                          ✓ Uploaded
                        </small>
                      </>
                    ) : (
                      <span>{isUploading ? "Uploading..." : "Upload Tunch Form"}</span>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleT2TImageChange}
                      disabled={isUploading}
                    />
                  </div>
                  <div className="difference-container">
                    <label>Difference (gm)</label>
                    <div className="difference-box">
                      <button type="button" onClick={decreaseDifference}>−</button>
                      <input
                        type="number"
                        step="0.1"
                        value={difference}
                        readOnly
                      />
                      <button type="button" onClick={increaseDifference}>+</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* DIMENSIONS */}
            <div className="form-section full-width">
              <h3>Dimensions</h3>
              <div className="dimensions-grid">
                <div className="dimension-item">
                  <label>Length (mm)</label>
                  <input
                    type="number"
                    name="length"
                    placeholder="Enter length"
                    value={dimensions.length === 0 || dimensions.length === "" ? "" : dimensions.length}
                    onChange={handleChange}
                    step="0.01"
                  />
                </div>
                <div className="dimension-item">
                  <label>Width (mm)</label>
                  <input
                    type="number"
                    name="width"
                    placeholder="Enter width"
                    value={dimensions.width === 0 || dimensions.width === "" ? "" : dimensions.width}
                    onChange={handleChange}
                    step="0.01"
                  />
                </div>
                <div className="dimension-item">
                  <label>Height (mm)</label>
                  <input
                    type="number"
                    name="height"
                    placeholder="Enter height"
                    value={dimensions.height === 0 || dimensions.height === "" ? "" : dimensions.height}
                    onChange={handleChange}
                    step="0.01"
                  />
                </div>
                <div className="dimension-item">
                  <label>Diameter (mm)</label>
                  <input
                    type="number"
                    name="diameter"
                    placeholder="Enter diameter"
                    value={dimensions.diameter === 0 || dimensions.diameter === "" ? "" : dimensions.diameter}
                    onChange={handleChange}
                    step="0.01"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* DESCRIPTION */}
        <div className="form-section">
          <h3>Description</h3>
          <div className="description-container">
            <textarea
              name="description"
              placeholder="Enter product description..."
              value={productData.description || ""}
              onChange={handleChange}
              rows={4}
              className="description-textarea"
            />
          </div>
        </div>

        {/* IMAGE */}
        <div className="form-section">
          <h3>Product Image</h3>
          <div className="image-upload-box">
            {previewImage ? (
              <>
                <img
                  src={previewImage}
                  alt="Product Preview"
                  className="preview-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (!previewImage.startsWith('data:') && !previewImage.includes(BASE_URL)) {
                      const cleanPath = previewImage.startsWith('/') ? previewImage.slice(1) : previewImage;
                      target.src = `${BASE_URL}/${cleanPath}`;
                    } else {
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        const fallback = document.createElement('span');
                        fallback.textContent = '⚠️ Image not available';
                        fallback.style.color = '#ef4444';
                        parent.appendChild(fallback);
                      }
                    }
                  }}
                />
                {isUploading && <small style={{ color: '#f59e0b' }}>Uploading...</small>}
                {uploadedImageUrl && !isUploading && (
                  <small style={{ color: '#10b981', display: 'block' }}>✓ Uploaded</small>
                )}
              </>
            ) : (
              <span>{isUploading ? "Uploading..." : "Click to upload new image"}</span>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isUploading}
            />
          </div>
        </div>

        {/* AVAILABILITY */}
        <div className="form-section">
          <h3>Availability Status</h3>
          <div className="toggle-buttons availability-toggle">
            <button
              type="button"
              className={availabilityStatus === "Available" ? "active-btn" : ""}
              onClick={() => setAvailabilityStatus("Available")}
            >
              Available
            </button>
            <button
              type="button"
              className={availabilityStatus === "Not Available" ? "active-btn" : ""}
              onClick={() => setAvailabilityStatus("Not Available")}
            >
              Not Available
            </button>
          </div>
        </div>

        <div className="form-actions1">
          <button
            type="button"
            className="cancel-btn"
            onClick={() => navigate("/product/list")}
          >
            Close
          </button>

          <button
            type="submit"
            className="submit-btn"
            disabled={isUploading || isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update Product"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProduct;